import { useState, useEffect, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';
const STORAGE_KEY = 'bodega_reseller_orders';
const FX_CACHE_KEY = 'bodega_reseller_fx';
const HOME_CURRENCY_KEY = 'bodega_reseller_home_currency';
const FX_TTL = 60 * 60 * 1000; // 1 hour

const CURRENCIES = ['AUD', 'USD', 'GBP', 'CAD', 'EUR'];

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

function loadOrders() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
  catch { return []; }
}

function saveOrders(orders) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(orders));
}

function loadHomeCurrency() {
  return localStorage.getItem(HOME_CURRENCY_KEY) || 'AUD';
}

function blankItem() {
  return {
    id: uid(),
    name: '',
    type: 'Box',
    qty: 1,
    purchasePrice: '',
    purchaseCurrency: 'USD',
    packsPerBox: '',
    postage: '',
    postageCurrency: 'USD',
    shippingCharged: '',
    shippingChargedCurrency: 'USD',
    postagePerPack: '',
    postagePerPackCurrency: 'USD',
    shippingChargedPerPack: '',
    shippingChargedPerPackCurrency: 'USD',
    sellStrategy: '',
  };
}

function blankOrder() {
  return {
    id: uid(),
    supplier: '',
    orderNumber: '',
    shippingAmount: '',
    shippingCurrency: 'USD',
    items: [blankItem()],
  };
}

// Convert amount from any currency into homeCurrency.
// fxRates = rates fetched relative to homeCurrency: { USD: X, GBP: Y, ... }
// meaning 1 homeCurrency = X of that foreign currency.
// So: foreignAmount / rate = homeAmount
function toHome(amount, currency, homeCurrency, fxRates) {
  if (currency === homeCurrency) return amount;
  const rate = fxRates?.[currency];
  if (!rate) return amount;
  return amount / rate;
}

function calcItem(order, item, homeCurrency, fxRates) {
  const qty = parseFloat(item.qty) || 1;
  const purchasePrice = parseFloat(item.purchasePrice) || 0;

  // Allocate import shipping proportionally by total purchase value (price × qty)
  const totalOrderValue = order.items.reduce((sum, i) => {
    const p = parseFloat(i.purchasePrice) || 0;
    const q = parseFloat(i.qty) || 1;
    return sum + (p * q);
  }, 0);
  const itemValue = purchasePrice * qty;
  const valueFraction = totalOrderValue > 0 ? itemValue / totalOrderValue : 0;
  const shippingPerUnit = totalOrderValue > 0
    ? ((parseFloat(order.shippingAmount) || 0) * valueFraction) / qty
    : 0;

  const purchasePriceHome = toHome(purchasePrice, item.purchaseCurrency || 'USD', homeCurrency, fxRates);
  const shippingPerUnitHome = toHome(shippingPerUnit, order.shippingCurrency || 'USD', homeCurrency, fxRates);
  const importCostHome = purchasePriceHome + shippingPerUnitHome;
  const postage = parseFloat(item.postage) || 0;
  const postageHome = toHome(postage, item.postageCurrency || homeCurrency, homeCurrency, fxRates);
  const shippingCharged = parseFloat(item.shippingCharged) || 0;
  const shippingChargedHome = toHome(shippingCharged, item.shippingChargedCurrency || homeCurrency, homeCurrency, fxRates);
  const netPostageHome = postageHome - shippingChargedHome; // buyer contribution offsets cost
  const totalUnitCost = importCostHome + netPostageHome;
  const breakEven = totalUnitCost / 0.80;

  let packBreakEven = null;
  let packCost = null;
  let packPostageHome = null;
  let packShippingChargedHome = null;
  let packNetPostageHome = null;
  if (item.type === 'Box') {
    const packs = parseFloat(item.packsPerBox) || null;
    if (packs) {
      const rawPackPostage = parseFloat(item.postagePerPack) || 0;
      packPostageHome = toHome(rawPackPostage, item.postagePerPackCurrency || homeCurrency, homeCurrency, fxRates);
      const rawPackShippingCharged = parseFloat(item.shippingChargedPerPack) || 0;
      packShippingChargedHome = toHome(rawPackShippingCharged, item.shippingChargedPerPackCurrency || homeCurrency, homeCurrency, fxRates);
      packNetPostageHome = packPostageHome - packShippingChargedHome;
      packCost = (importCostHome / packs) + packNetPostageHome;
      packBreakEven = packCost / 0.80;
    }
  }

  return {
    purchasePrice, purchasePriceHome,
    shippingPerUnit, shippingPerUnitHome,
    importCostHome,
    postage, postageHome, shippingCharged, shippingChargedHome, netPostageHome,
    totalUnitCost, breakEven,
    packCost, packPostageHome, packShippingChargedHome, packNetPostageHome, packBreakEven, qty,
  };
}

function fmt(n, decimals = 2) {
  if (n == null || isNaN(n)) return '—';
  return `$${n.toFixed(decimals)}`;
}

function CalcRow({ label, value, highlight, theme }) {
  const text = theme === 'dark' ? '#f0ede8' : '#1a1916';
  const text2 = theme === 'dark' ? '#857f76' : '#6b6760';
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
      <span style={{ fontSize: 12, color: text2 }}>{label}</span>
      <span style={{ fontSize: 13, fontFamily: 'IBM Plex Mono, monospace', color: highlight ? '#4a9b6f' : text, fontWeight: highlight ? 600 : 400 }}>{value}</span>
    </div>
  );
}

function ProfitCalc({ totalUnitCost, homeCurrency, theme }) {
  const [sellPrice, setSellPrice] = useState('');
  const bg = theme === 'dark' ? '#1c1b18' : '#ffffff';
  const border = theme === 'dark' ? '#2e2c28' : '#dddad4';
  const text = theme === 'dark' ? '#f0ede8' : '#1a1916';
  const text2 = theme === 'dark' ? '#857f76' : '#6b6760';
  const green = '#4a9b6f';

  const sell = parseFloat(sellPrice) || 0;
  const profit = sell > 0 ? sell * 0.80 - totalUnitCost : null;
  const margin = sell > 0 ? (profit / sell) * 100 : null;

  return (
    <div style={{ marginTop: 8, padding: '12px 14px', background: theme === 'dark' ? '#141412' : '#f7f6f3', borderRadius: 6, border: `1px solid ${border}` }}>
      <div style={{ fontSize: 10, color: text2, fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 8 }}>Profit calculator</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: text2 }}>Sell price ({homeCurrency}) $</span>
        <input
          value={sellPrice}
          onChange={e => setSellPrice(e.target.value)}
          placeholder="0.00"
          type="number"
          min="0"
          step="0.01"
          style={{ width: 80, height: 28, padding: '0 8px', borderRadius: 5, border: `1px solid ${border}`, background: bg, color: text, fontSize: 13, fontFamily: 'IBM Plex Mono, monospace', outline: 'none' }}
        />
        {sell > 0 && (
          <div style={{ display: 'flex', gap: 12 }}>
            <span style={{ fontSize: 13, fontFamily: 'IBM Plex Mono, monospace', color: profit >= 0 ? green : '#b94040' }}>
              {profit >= 0 ? '+' : ''}{fmt(profit)} profit
            </span>
            <span style={{ fontSize: 12, color: text2 }}>
              ({margin >= 0 ? '+' : ''}{margin?.toFixed(1)}% margin)
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function CurrencyInput({ label, value, currency, onValueChange, onCurrencyChange, placeholder = '0.00', theme }) {
  const border = theme === 'dark' ? '#2e2c28' : '#dddad4';
  const bg = theme === 'dark' ? '#1c1b18' : '#ffffff';
  const text = theme === 'dark' ? '#f0ede8' : '#1a1916';
  const text2 = theme === 'dark' ? '#857f76' : '#6b6760';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ fontSize: 10, color: text2, fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '0.05em', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ display: 'flex', height: 32, border: `1px solid ${border}`, borderRadius: 5, overflow: 'hidden' }}>
        <select
          value={currency}
          onChange={e => onCurrencyChange(e.target.value)}
          style={{ border: 'none', borderRight: `1px solid ${border}`, background: theme === 'dark' ? '#1a1917' : '#eeecea', color: text2, fontSize: 11, fontFamily: 'IBM Plex Mono, monospace', outline: 'none', padding: '0 6px', cursor: 'pointer', flexShrink: 0 }}>
          {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <input
          value={value}
          onChange={e => onValueChange(e.target.value)}
          placeholder={placeholder}
          type="number"
          min="0"
          step="any"
          style={{ flex: 1, border: 'none', background: bg, color: text, fontSize: 12, fontFamily: 'IBM Plex Mono, monospace', outline: 'none', padding: '0 10px', minWidth: 0 }}
        />
      </div>
    </div>
  );
}

function ItemCard({ order, item, homeCurrency, fxRates, theme, onChange, onRemove, canRemove }) {
  const border = theme === 'dark' ? '#2e2c28' : '#dddad4';
  const bg = theme === 'dark' ? '#1c1b18' : '#ffffff';
  const bg2 = theme === 'dark' ? '#141412' : '#f7f6f3';
  const text = theme === 'dark' ? '#f0ede8' : '#1a1916';
  const text2 = theme === 'dark' ? '#857f76' : '#6b6760';

  const calc = calcItem(order, item, homeCurrency, fxRates);
  const hasCalc = parseFloat(item.purchasePrice) > 0;

  const inp = (key, placeholder, type = 'text', width = '100%') => (
    <input
      value={item[key]}
      onChange={e => onChange({ ...item, [key]: e.target.value })}
      placeholder={placeholder}
      type={type}
      min={type === 'number' ? '0' : undefined}
      step={type === 'number' ? 'any' : undefined}
      style={{ height: 32, padding: '0 10px', borderRadius: 5, border: `1px solid ${border}`, background: bg, color: text, fontSize: 12, fontFamily: 'IBM Plex Sans, sans-serif', outline: 'none', width, boxSizing: 'border-box' }}
    />
  );

  return (
    <div style={{ border: `1px solid ${border}`, borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 10, color: text2, fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 4 }}>Item name</div>
            {inp('name', 'e.g. 1991-92 Upper Deck Low Series Box')}
          </div>
          <div>
            <div style={{ fontSize: 10, color: text2, fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 4 }}>Type</div>
            <select value={item.type} onChange={e => onChange({ ...item, type: e.target.value })}
              style={{ height: 32, padding: '0 8px', borderRadius: 5, border: `1px solid ${border}`, background: bg, color: text, fontSize: 12, outline: 'none' }}>
              <option value="Box">Box</option>
              <option value="Pack">Pack</option>
              <option value="FactorySet">Factory Set</option>
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10, color: text2, fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 4 }}>Sell strategy</div>
            <select value={item.sellStrategy} onChange={e => onChange({ ...item, sellStrategy: e.target.value })}
              style={{ height: 32, padding: '0 8px', borderRadius: 5, border: `1px solid ${border}`, background: bg, color: item.sellStrategy ? text : text2, fontSize: 12, outline: 'none' }}>
              <option value="">— select —</option>
              <option value="Per Box">Per Box</option>
              <option value="Per Pack">Per Pack</option>
              <option value="Both">Both</option>
              <option value="Factory Set">Factory Set</option>
            </select>
          </div>
          <div style={{ width: 60 }}>
            <div style={{ fontSize: 10, color: text2, fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 4 }}>Qty</div>
            {inp('qty', '1', 'number', 60)}
          </div>
          {canRemove && (
            <button onClick={onRemove} style={{ marginTop: 20, width: 28, height: 28, borderRadius: 5, border: `1px solid ${border}`, background: 'transparent', color: text2, fontSize: 14, cursor: 'pointer', flexShrink: 0 }}>×</button>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 160 }}>
            <CurrencyInput
              label="Purchase price / unit"
              value={item.purchasePrice}
              currency={item.purchaseCurrency || 'USD'}
              onValueChange={v => onChange({ ...item, purchasePrice: v })}
              onCurrencyChange={c => onChange({ ...item, purchaseCurrency: c })}
              theme={theme}
            />
          </div>
          {item.type === 'Box' && (
            <div style={{ flex: 1, minWidth: 100 }}>
              <div style={{ fontSize: 10, color: text2, fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 4 }}>Packs per box</div>
              {inp('packsPerBox', 'e.g. 36', 'number')}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 140 }}>
            <CurrencyInput
              label={item.type === 'Box' && item.packsPerBox ? 'Your postage cost (box)' : 'Your postage cost'}
              value={item.postage}
              currency={item.postageCurrency || homeCurrency}
              onValueChange={v => onChange({ ...item, postage: v })}
              onCurrencyChange={c => onChange({ ...item, postageCurrency: c })}
              theme={theme}
            />
          </div>
          <div style={{ flex: 1, minWidth: 140 }}>
            <CurrencyInput
              label={item.type === 'Box' && item.packsPerBox ? 'Shipping charged to buyer (box)' : 'Shipping charged to buyer'}
              value={item.shippingCharged}
              currency={item.shippingChargedCurrency || homeCurrency}
              onValueChange={v => onChange({ ...item, shippingCharged: v })}
              onCurrencyChange={c => onChange({ ...item, shippingChargedCurrency: c })}
              theme={theme}
            />
          </div>
          {item.type === 'Box' && item.packsPerBox && (<>
            <div style={{ flex: 1, minWidth: 140 }}>
              <CurrencyInput
                label="Your postage cost (per pack)"
                value={item.postagePerPack}
                currency={item.postagePerPackCurrency || homeCurrency}
                onValueChange={v => onChange({ ...item, postagePerPack: v })}
                onCurrencyChange={c => onChange({ ...item, postagePerPackCurrency: c })}
                theme={theme}
              />
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <CurrencyInput
                label="Shipping charged to buyer (per pack)"
                value={item.shippingChargedPerPack}
                currency={item.shippingChargedPerPackCurrency || homeCurrency}
                onValueChange={v => onChange({ ...item, shippingChargedPerPack: v })}
                onCurrencyChange={c => onChange({ ...item, shippingChargedPerPackCurrency: c })}
                theme={theme}
              />
            </div>
          </>)}
        </div>
      </div>

      {/* Calc results */}
      {hasCalc && (
        <div style={{ background: bg2, borderTop: `1px solid ${border}`, padding: '12px 16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: item.type === 'Box' && calc.packBreakEven ? '1fr 1fr' : '1fr', gap: 16 }}>
            <div>
              <div style={{ fontSize: 10, color: text2, fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 6 }}>
                {item.type === 'Box' ? 'Per box' : item.type === 'Pack' ? 'Per pack' : 'Per set'}
              </div>
              <CalcRow label={`Purchase price (${item.purchaseCurrency || 'USD'})`} value={fmt(calc.purchasePrice)} theme={theme} />
              {(item.purchaseCurrency || 'USD') !== homeCurrency && <CalcRow label={`Purchase price (${homeCurrency})`} value={fmt(calc.purchasePriceHome)} theme={theme} />}
              <CalcRow label={`Import shipping (${order.shippingCurrency || 'USD'}, your share)`} value={fmt(calc.shippingPerUnit)} theme={theme} />
              {(order.shippingCurrency || 'USD') !== homeCurrency && <CalcRow label={`Import shipping (${homeCurrency}, your share)`} value={fmt(calc.shippingPerUnitHome)} theme={theme} />}
              <CalcRow label={`Import cost (${homeCurrency})`} value={fmt(calc.importCostHome)} theme={theme} />
              <CalcRow label={`Your postage cost (${item.postageCurrency || homeCurrency})`} value={fmt(calc.postage)} theme={theme} />
              {(item.postageCurrency || homeCurrency) !== homeCurrency && <CalcRow label={`Your postage cost (${homeCurrency})`} value={fmt(calc.postageHome)} theme={theme} />}
              <CalcRow label={`Shipping charged to buyer (${item.shippingChargedCurrency || homeCurrency})`} value={`−${fmt(calc.shippingCharged)}`} theme={theme} />
              {(item.shippingChargedCurrency || homeCurrency) !== homeCurrency && <CalcRow label={`Shipping charged to buyer (${homeCurrency})`} value={`−${fmt(calc.shippingChargedHome)}`} theme={theme} />}
              <CalcRow label={`Net postage cost (${homeCurrency})`} value={fmt(calc.netPostageHome)} theme={theme} />
              <CalcRow label={`Total unit cost (${homeCurrency})`} value={fmt(calc.totalUnitCost)} theme={theme} />
              <div style={{ borderTop: `1px solid ${border}`, marginTop: 6, paddingTop: 6 }}>
                <CalcRow label={`Break even — min. sell price (${homeCurrency}, after 20% fees)`} value={fmt(calc.breakEven)} highlight theme={theme} />
              </div>
              <ProfitCalc totalUnitCost={calc.totalUnitCost} homeCurrency={homeCurrency} theme={theme} />
            </div>
            {item.type === 'Box' && calc.packBreakEven && (
              <div>
                <div style={{ fontSize: 10, color: text2, fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 6 }}>Per pack</div>
                <CalcRow label={`Import cost per pack (${homeCurrency})`} value={fmt(calc.importCostHome / (parseFloat(item.packsPerBox) || 1))} theme={theme} />
                <CalcRow label={`Your postage cost (${item.postagePerPackCurrency || homeCurrency})`} value={fmt(parseFloat(item.postagePerPack) || 0)} theme={theme} />
                {(item.postagePerPackCurrency || homeCurrency) !== homeCurrency && <CalcRow label={`Your postage cost (${homeCurrency})`} value={fmt(calc.packPostageHome)} theme={theme} />}
                <CalcRow label={`Shipping charged to buyer (${item.shippingChargedPerPackCurrency || homeCurrency})`} value={`−${fmt(parseFloat(item.shippingChargedPerPack) || 0)}`} theme={theme} />
                {(item.shippingChargedPerPackCurrency || homeCurrency) !== homeCurrency && <CalcRow label={`Shipping charged to buyer (${homeCurrency})`} value={`−${fmt(calc.packShippingChargedHome)}`} theme={theme} />}
                <CalcRow label={`Net postage cost (${homeCurrency})`} value={fmt(calc.packNetPostageHome)} theme={theme} />
                <CalcRow label={`Total pack cost (${homeCurrency})`} value={fmt(calc.packCost)} theme={theme} />
                <div style={{ borderTop: `1px solid ${border}`, marginTop: 6, paddingTop: 6 }}>
                  <CalcRow label={`Break even — min. sell price (${homeCurrency}, after 20% fees)`} value={fmt(calc.packBreakEven)} highlight theme={theme} />
                </div>
                <ProfitCalc totalUnitCost={calc.packCost} homeCurrency={homeCurrency} theme={theme} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function OrderDetail({ order, homeCurrency, fxRates, theme, onChange }) {
  const [copied, setCopied] = useState(false);
  const border = theme === 'dark' ? '#2e2c28' : '#dddad4';
  const bg = theme === 'dark' ? '#141412' : '#f7f6f3';
  const bg2 = theme === 'dark' ? '#1c1b18' : '#ffffff';
  const text = theme === 'dark' ? '#f0ede8' : '#1a1916';
  const text2 = theme === 'dark' ? '#857f76' : '#6b6760';

  const updateItem = (id, updated) => onChange({ ...order, items: order.items.map(i => i.id === id ? updated : i) });
  const removeItem = (id) => onChange({ ...order, items: order.items.filter(i => i.id !== id) });
  const addItem = () => onChange({ ...order, items: [...order.items, blankItem()] });

  const inp = (key, placeholder, label) => (
    <div>
      <div style={{ fontSize: 10, color: text2, fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
      <input
        value={order[key]}
        onChange={e => onChange({ ...order, [key]: e.target.value })}
        placeholder={placeholder}
        style={{ height: 32, padding: '0 10px', borderRadius: 5, border: `1px solid ${border}`, background: bg2, color: text, fontSize: 12, fontFamily: 'IBM Plex Sans, sans-serif', outline: 'none', width: '100%', boxSizing: 'border-box' }}
      />
    </div>
  );

  const exportCSV = () => {
    const headers = [
      'Item Name', 'Type', 'Sell Strategy', 'Qty',
      `Purchase Price (${homeCurrency})`, `Import Shipping (${homeCurrency})`, `Import Cost (${homeCurrency})`,
      `Postage Cost (${homeCurrency})`, `Shipping Charged to Buyer (${homeCurrency})`, `Net Postage (${homeCurrency})`,
      `Total Unit Cost (${homeCurrency})`, `Break Even Box (${homeCurrency})`,
      'Packs Per Box', `Import Cost Per Pack (${homeCurrency})`,
      `Pack Postage (${homeCurrency})`, `Pack Shipping Charged (${homeCurrency})`, `Pack Net Postage (${homeCurrency})`,
      `Total Pack Cost (${homeCurrency})`, `Break Even Pack (${homeCurrency})`,
    ];
    const rows = order.items.map(item => {
      const c = calcItem(order, item, homeCurrency, fxRates);
      return [
        item.name, item.type, item.sellStrategy || '', item.qty,
        c.purchasePriceHome?.toFixed(2) ?? '',
        c.shippingPerUnitHome?.toFixed(2) ?? '',
        c.importCostHome?.toFixed(2) ?? '',
        c.postageHome?.toFixed(2) ?? '',
        c.shippingChargedHome?.toFixed(2) ?? '',
        c.netPostageHome?.toFixed(2) ?? '',
        c.totalUnitCost?.toFixed(2) ?? '',
        c.breakEven?.toFixed(2) ?? '',
        item.packsPerBox || '',
        c.packBreakEven ? (c.importCostHome / (parseFloat(item.packsPerBox) || 1)).toFixed(2) : '',
        c.packBreakEven ? c.packPostageHome?.toFixed(2) : '',
        c.packBreakEven ? c.packShippingChargedHome?.toFixed(2) : '',
        c.packBreakEven ? c.packNetPostageHome?.toFixed(2) : '',
        c.packCost?.toFixed(2) ?? '',
        c.packBreakEven?.toFixed(2) ?? '',
      ].map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',');
    });
    const label = order.supplier || order.orderNumber || 'order';
    const date = new Date().toISOString().slice(0, 10);
    const csv = [headers.map(h => `"${h}"`).join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${label.replace(/[^a-z0-9]/gi, '_')}_${date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = () => {
    const header = [order.supplier, order.orderNumber].filter(Boolean).join(' — ') || 'Untitled order';
    const lines = [`${header}`, ''];
    order.items.forEach(item => {
      const c = calcItem(order, item, homeCurrency, fxRates);
      const name = item.name || 'Unnamed item';
      const qty = item.qty > 1 ? ` (×${item.qty})` : '';
      lines.push(`${name}${qty}`);
      if (c.breakEven) lines.push(`  Break even (box): ${homeCurrency} ${c.breakEven.toFixed(2)}`);
      if (c.packBreakEven) lines.push(`  Break even (pack): ${homeCurrency} ${c.packBreakEven.toFixed(2)}`);
      lines.push('');
    });
    navigator.clipboard.writeText(lines.join('\n').trim()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Order header fields */}
      <div style={{ background: bg2, border: `1px solid ${border}`, borderRadius: 8, padding: '16px', display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: 2, minWidth: 160 }}>{inp('supplier', 'e.g. Blowout Cards', 'Supplier')}</div>
        <div style={{ flex: 1, minWidth: 120 }}>{inp('orderNumber', 'e.g. BO-123456', 'Order number')}</div>
        <div style={{ minWidth: 180 }}>
          <CurrencyInput
            label="Import shipping"
            value={order.shippingAmount}
            currency={order.shippingCurrency || 'USD'}
            onValueChange={v => onChange({ ...order, shippingAmount: v })}
            onCurrencyChange={c => onChange({ ...order, shippingCurrency: c })}
            theme={theme}
          />
        </div>
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <button onClick={copyToClipboard} title="Copy summary to clipboard"
            style={{ height: 32, padding: '0 12px', borderRadius: 5, border: `1px solid ${border}`, background: 'transparent', color: copied ? '#4a9b6f' : text2, fontSize: 12, cursor: 'pointer', fontFamily: 'IBM Plex Mono, monospace', whiteSpace: 'nowrap' }}>
            {copied ? '✓ Copied' : '⎘ Copy'}
          </button>
          <button onClick={exportCSV} title="Export order as CSV"
            style={{ height: 32, padding: '0 12px', borderRadius: 5, border: `1px solid ${border}`, background: 'transparent', color: text2, fontSize: 12, cursor: 'pointer', fontFamily: 'IBM Plex Mono, monospace', whiteSpace: 'nowrap' }}>
            ↓ CSV
          </button>
        </div>
      </div>

      {/* Items */}
      {order.items.map(item => (
        <ItemCard
          key={item.id}
          order={order}
          item={item}
          homeCurrency={homeCurrency}
          fxRates={fxRates}
          theme={theme}
          onChange={updated => updateItem(item.id, updated)}
          onRemove={() => removeItem(item.id)}
          canRemove={order.items.length > 1}
        />
      ))}
      <button onClick={addItem} style={{ height: 32, borderRadius: 5, border: `1px dashed ${border}`, background: 'transparent', color: text2, fontSize: 12, cursor: 'pointer', fontFamily: 'IBM Plex Mono, monospace' }}>
        + Add item to order
      </button>
    </div>
  );
}

export default function Reseller({ theme, getToken }) {
  const [orders, setOrders] = useState(loadOrders);
  const [selectedOrderId, setSelectedOrderId] = useState(() => {
    const saved = loadOrders();
    return saved.length > 0 ? saved[0].id : null;
  });
  const [homeCurrency, setHomeCurrency] = useState(loadHomeCurrency);
  const [fxRates, setFxRates] = useState(null);
  const [fxLoading, setFxLoading] = useState(false);
  const [fxError, setFxError] = useState(null);
  const [fxUpdated, setFxUpdated] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null); // 'saving' | 'saved' | 'error'

  const border = theme === 'dark' ? '#2e2c28' : '#dddad4';
  const bg = theme === 'dark' ? '#141412' : '#f7f6f3';
  const bg2 = theme === 'dark' ? '#1c1b18' : '#ffffff';
  const text = theme === 'dark' ? '#f0ede8' : '#1a1916';
  const text2 = theme === 'dark' ? '#857f76' : '#6b6760';

  const fetchFx = useCallback(async (force = false, currency = null) => {
    const home = currency || homeCurrency;
    const cacheKey = `${FX_CACHE_KEY}_${home}`;
    if (!force) {
      try {
        const cached = JSON.parse(localStorage.getItem(cacheKey) || 'null');
        if (cached && Date.now() - cached.ts < FX_TTL) {
          setFxRates(cached.rates);
          setFxUpdated(new Date(cached.ts));
          return;
        }
      } catch {}
    }
    setFxLoading(true);
    setFxError(null);
    try {
      const others = CURRENCIES.filter(c => c !== home).join(',');
      const res = await fetch(`${API_BASE}/api/fx/rates?from=${home}&to=${others}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setFxRates(data.rates);
      const ts = Date.now();
      setFxUpdated(new Date(ts));
      localStorage.setItem(cacheKey, JSON.stringify({ rates: data.rates, ts }));
    } catch {
      setFxError('Could not fetch exchange rates.');
    }
    setFxLoading(false);
  }, [homeCurrency]);

  const handleHomeCurrencyChange = (c) => {
    setHomeCurrency(c);
    localStorage.setItem(HOME_CURRENCY_KEY, c);
    fetchFx(true, c);
  };

  useEffect(() => { fetchFx(); }, [fetchFx]);

  // Load from server on mount
  useEffect(() => {
    if (!getToken) return;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/api/user/reseller-orders`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0) {
            setOrders(data);
            saveOrders(data);
            setSelectedOrderId(id => id || data[0].id);
          }
        }
      } catch {}
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Save to localStorage + server on every change
  useEffect(() => {
    saveOrders(orders);
    if (!getToken) return;
    setSyncStatus('saving');
    (async () => {
      try {
        const token = await getToken();
        await fetch(`${API_BASE}/api/user/reseller-orders`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(orders),
        });
        setSyncStatus('saved');
        setTimeout(() => setSyncStatus(null), 2000);
      } catch {
        setSyncStatus('error');
      }
    })();
  }, [orders]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateOrder = (id, updated) => setOrders(prev => prev.map(o => o.id === id ? updated : o));
  const removeOrder = (id) => {
    setOrders(prev => {
      const next = prev.filter(o => o.id !== id);
      if (selectedOrderId === id) {
        setSelectedOrderId(next.length > 0 ? next[0].id : null);
      }
      return next;
    });
  };
  const addOrder = () => {
    const o = blankOrder();
    setOrders(prev => [o, ...prev]);
    setSelectedOrderId(o.id);
  };

  const selectedOrder = orders.find(o => o.id === selectedOrderId) || null;

  const orderLabel = (order) => {
    const name = order.supplier || order.orderNumber || 'Untitled order';
    const n = order.items.length;
    return { name, count: n };
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 48px)', minHeight: 0 }}>
      {/* Top header bar */}
      <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, flexShrink: 0 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 20, fontWeight: 700, color: text }}>Reseller</div>
            {syncStatus === 'saving' && <div style={{ fontSize: 10, color: text2, fontFamily: 'IBM Plex Mono, monospace' }}>saving...</div>}
            {syncStatus === 'saved' && <div style={{ fontSize: 10, color: '#4a9b6f', fontFamily: 'IBM Plex Mono, monospace' }}>saved</div>}
            {syncStatus === 'error' && <div style={{ fontSize: 10, color: '#b94040', fontFamily: 'IBM Plex Mono, monospace' }}>save failed</div>}
          </div>
          <div style={{ fontSize: 11, color: text2, marginTop: 2 }}>Import cost calculator — break even and profit margins for sealed product.</div>
        </div>

        {/* FX rates + home currency */}
        <div style={{ background: bg2, border: `1px solid ${border}`, borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{ fontSize: 10, color: text2, fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 5 }}>Selling currency</div>
            <div style={{ display: 'flex', height: 26, border: `1px solid ${border}`, borderRadius: 5, overflow: 'hidden' }}>
              {CURRENCIES.map(c => (
                <button key={c} onClick={() => handleHomeCurrencyChange(c)}
                  style={{ border: 'none', borderRight: c !== CURRENCIES[CURRENCIES.length - 1] ? `1px solid ${border}` : 'none', background: homeCurrency === c ? text : 'transparent', color: homeCurrency === c ? bg : text2, fontSize: 10, fontFamily: 'IBM Plex Mono, monospace', cursor: 'pointer', padding: '0 7px' }}>
                  {c}
                </button>
              ))}
            </div>
            {fxRates && !fxError && (
              <div style={{ display: 'flex', gap: 12, marginTop: 6 }}>
                {Object.entries(fxRates).map(([c, rate]) => (
                  <div key={c}>
                    <div style={{ fontSize: 9, color: text2, fontFamily: 'IBM Plex Mono, monospace' }}>1 {c}</div>
                    <div style={{ fontSize: 12, fontFamily: 'IBM Plex Mono, monospace', fontWeight: 600, color: text }}>{fmt(1 / rate)}</div>
                  </div>
                ))}
              </div>
            )}
            {fxLoading && <div style={{ fontSize: 10, color: text2, fontFamily: 'IBM Plex Mono, monospace', marginTop: 4 }}>Loading rates...</div>}
            {fxError && <div style={{ fontSize: 10, color: '#b94040', fontFamily: 'IBM Plex Mono, monospace', marginTop: 4 }}>{fxError}</div>}
            {fxUpdated && !fxError && (
              <div style={{ fontSize: 9, color: text2, marginTop: 3 }}>
                Updated {fxUpdated.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
              </div>
            )}
          </div>
          <button onClick={() => fetchFx(true)} disabled={fxLoading} style={{ height: 26, padding: '0 8px', borderRadius: 5, border: `1px solid ${border}`, background: 'transparent', color: text2, fontSize: 12, cursor: fxLoading ? 'not-allowed' : 'pointer', fontFamily: 'IBM Plex Mono, monospace' }}>↺</button>
        </div>
      </div>

      {/* Two-column layout */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* Sidebar */}
        <div style={{ width: 240, flexShrink: 0, borderRight: `1px solid ${border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '12px 12px 8px' }}>
            <button onClick={addOrder} style={{ width: '100%', height: 32, borderRadius: 5, border: 'none', background: text, color: bg, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'IBM Plex Sans, sans-serif' }}>
              + New order
            </button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '4px 8px 12px' }}>
            {orders.length === 0 ? (
              <div style={{ padding: '24px 8px', textAlign: 'center', fontSize: 11, color: text2, fontFamily: 'IBM Plex Mono, monospace' }}>
                No orders yet.
              </div>
            ) : orders.map(order => {
              const { name, count } = orderLabel(order);
              const isSelected = order.id === selectedOrderId;
              return (
                <div key={order.id}
                  onClick={() => setSelectedOrderId(order.id)}
                  style={{ padding: '8px 10px', borderRadius: 6, marginBottom: 2, cursor: 'pointer', background: isSelected ? (theme === 'dark' ? '#252320' : '#eeece8') : 'transparent', border: `1px solid ${isSelected ? border : 'transparent'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, color: text, fontWeight: isSelected ? 500 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
                    {order.orderNumber && order.supplier && (
                      <div style={{ fontSize: 10, color: text2, fontFamily: 'IBM Plex Mono, monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 1 }}>{order.orderNumber}</div>
                    )}
                    <div style={{ fontSize: 10, color: text2, marginTop: 2 }}>{count} item{count !== 1 ? 's' : ''}</div>
                  </div>
                  <button
                    onClick={e => { e.stopPropagation(); removeOrder(order.id); }}
                    title="Delete order"
                    style={{ flexShrink: 0, width: 20, height: 20, borderRadius: 4, border: 'none', background: 'transparent', color: text2, fontSize: 13, cursor: 'pointer', lineHeight: 1, padding: 0, opacity: 0.5 }}>
                    ×
                  </button>
                </div>
              );
            })}
          </div>

          {/* Footer note */}
          <div style={{ padding: '8px 12px', borderTop: `1px solid ${border}`, fontSize: 9, color: text2, fontFamily: 'IBM Plex Mono, monospace', lineHeight: 1.5 }}>
            Break even = cost ÷ 0.80<br />
            Shipping split by item value
          </div>
        </div>

        {/* Detail panel */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
          {selectedOrder ? (
            <OrderDetail
              order={selectedOrder}
              homeCurrency={homeCurrency}
              fxRates={fxRates || {}}
              theme={theme}
              onChange={updated => updateOrder(selectedOrder.id, updated)}
            />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: text2, fontSize: 13, fontFamily: 'IBM Plex Mono, monospace' }}>
              Select an order or create a new one.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
